<script setup>
import { ref, onMounted } from 'vue'
import StudentResult from './StudentResult.vue'
import VerificationCode from './VerificationCode.vue'
import AppToast from "./AppToast.vue";
import SystemOfflinePage from './SystemOfflinePage.vue'
import { HTTP } from '@/Api/http-common';
import Cookies from 'js-cookie'
import { useRouter } from 'vue-router'
import { useSystemConfig } from '@/composables/useSystemConfig'

const router = useRouter()

const { config, fetchConfig, isLive } = useSystemConfig()

const reasonCodeMessages = {
  0: 'Success',
  2: 'خطأ في تنسيق البيانات أو التحقق من الصحة',
  103: 'خطأ في الفاتورة (رصيد غير كافٍ)',
  104: 'حساب المستخدم في حالة غير نشطة',
  105: 'المستخدم غير معروف',
  106: 'تم عكس الشحنة بالفعل',
  108: 'الرسوم تتجاوز حد المعاملة',
  115: 'تم الوصول إلى الحد الأقصى للسعر العالمي',
  116: 'تم الوصول إلى حد المعدل للمستخدم',
  117: 'المستخدم في حالة عدم أهلية دائمة',
  118: 'المستخدم غير مؤهل للحصول على فاتورة الناقل',
  119: 'فشل متعلق بالمستخدم',
  200: 'رمز PIN غير صالح',
  201: 'انتهت صلاحية كلمة المرور لمرة واحدة',
  202: 'تم الوصول إلى الحد الأقصى لمحاولات التحقق من OTP',
  123: 'في تَقَدم',
  1005: 'رقم الاستمارة او الجلوس غير موجود'
};

const seatNumber = ref('')
const phoneNumber = ref('')
const selectedResultType = ref('') // Default to empty, must select
const showResult = ref(false)
const showVerification = ref(false)
const loading = ref(false)
const maxLengthSeat = ref(20);
const maxLengthPhone = ref(14);

// Store the real result from API
const realResult = ref(null)

const showToast = ref(false);
const toastMessage = ref('');
const toastType = ref('success');


// Constants for timeouts
const TOAST_TIMEOUT = 3000
const SUCCESS_NAVIGATION_TIMEOUT = 1200
const ALREADY_VERIFIED_TIMEOUT = 1500

// Helper function to show error messages
const showErrorToast = (message) => {
  toastMessage.value = message
  toastType.value = 'error'
  showToast.value = true
  setTimeout(() => {
    showToast.value = false
  }, TOAST_TIMEOUT)
}

const filterDigits = (value) => value.replace(/\D/g, '')

const handleSeatInput = (e) => {
  seatNumber.value = filterDigits(e.target.value)
  if (seatNumber.value.length > maxLengthSeat.value) {
    seatNumber.value = seatNumber.value.slice(0, maxLengthSeat.value);
  }
}

const handlePhoneInput = (e) => {
  let val = e.target.value;
  // Allow + only at the start for +249, otherwise only digits
  if (val.startsWith('+')) {
    val = '+' + filterDigits(val.slice(1));
    if (/^\+249(92|93|99)/.test(val)) {
      val = val.slice(0, 8) + val.slice(8, 15).replace(/[^\d]/g, '').slice(0, 7);
      if (val.length > 12) val = val.slice(0, 12);
    } else {
      val = val.slice(0, 13);
    }
  } else if (
    val.startsWith('092') || val.startsWith('093') || val.startsWith('099') ||
    val.startsWith('92') || val.startsWith('93') || val.startsWith('99')
  ) {
    let prefix = '';
    let rest = '';
    if (val.startsWith('092') || val.startsWith('093') || val.startsWith('099')) {
      prefix = val.slice(0, 3);
      rest = val.slice(3, 10).replace(/[^\d]/g, '').slice(0, 7);
      val = prefix + rest;
    } else if (val.startsWith('92') || val.startsWith('93') || val.startsWith('99')) {
      prefix = val.slice(0, 2);
      rest = val.slice(2, 9).replace(/[^\d]/g, '').slice(0, 7);
      val = prefix + rest;
    }
  } else {
    val = filterDigits(val);
  }
  phoneNumber.value = val;
  if (phoneNumber.value.length > maxLengthPhone.value) {
    phoneNumber.value = phoneNumber.value.slice(0, maxLengthPhone.value);
  }
}

function isValidMTNPhone(number) {
  // Accepts only valid MTN Sudan numbers starting with 92, 93, or 99
  // 092xxxxxxx, 093xxxxxxx, 099xxxxxxx, 92xxxxxxx, 93xxxxxxx, 99xxxxxxx, +24992xxxxxxx, +24993xxxxxxx, +24999xxxxxxx, 24992xxxxxxx, 24993xxxxxxx, 24999xxxxxxx
  const patterns = [
    /^0?(92|93|99)\d{7}$/,
    /^\+249(92|93|99)\d{7}$/,
    /^249(92|93|99)\d{7}$/
  ];
  return patterns.some(re => re.test(number));
}

function normalizePhoneNumber(number) {
  if (number.startsWith('+249')) {
    return number.slice(4); // Remove '+249'
  } else if (number.startsWith('249')) {
    return number.slice(3); // Remove '249'
  } else if (number.startsWith('0')) {
    return number.slice(1); // Remove leading '0'
  } else {
    return number; // Already normalized
  }
}

const handleSubmit = async () => {
  if (!seatNumber.value || !phoneNumber.value) {
    showErrorToast('الرجاء إدخال رقم الجلوس ورقم الهاتف')
    return
  }
  
  
  const normalizedPhone = '249' + normalizePhoneNumber(phoneNumber.value);
  if (!isValidMTNPhone(phoneNumber.value)) {
    showErrorToast('يرجى إدخال رقم هاتف MTN صحيح (يبدأ بـ 92 أو 93 أو 99)')
    return
  }
  loading.value = true

  try {
    const response = await HTTP.post(
      'SubmitSeat.php',
      {
        GenerateOTP: {
          phone_number: normalizedPhone,
          seat_number: seatNumber.value
        }
      }
    );

    const data = response.data;
    
    // Handle new error structure with error_code
    if (data && data.error === true && data.error_code) {
      // Handle specific error codes
      if (data.error_code === 1005) {
        showErrorToast(data.message_ar || reasonCodeMessages[1005] || 'رقم الاستمارة او الجلوس غير موجود')
      } else {
        showErrorToast(data.message_ar || `خطأ: ${data.error_code}`)
      }
      return
    }
    
    // Handle new API error codes and statuses
    if (data && typeof data.reasonCode !== 'undefined') {
      const mappedMsg = reasonCodeMessages[data.reasonCode];
      if (data.status === 'OK' && data.reasonCode === 0 && data.error === false) {
        // Success, OTP sent
        Cookies.set('seat_number', seatNumber.value)
        Cookies.set('phone_number', normalizedPhone)
        Cookies.set('requestID', data.request_id)
        // Navigate to verification page
        toastMessage.value = data.message_ar || 'تم إرسال رمز التحقق بنجاح.';
        toastType.value = 'success';
        showToast.value = true;
        setTimeout(() => {
          showToast.value = false;
          // Go to verification page after toast
          router.push({ name: 'verification' });
        }, SUCCESS_NAVIGATION_TIMEOUT);
      } else if (data.status === 'ALREADY_VERIFIED' && data.error === false && data.result) {
        // Already verified, show success message then navigate to result
        toastMessage.value = data.message_ar || 'تم التحقق من النتيجة مسبقاً، يتم عرضها مباشرة.';
        toastType.value = 'success';
        showToast.value = true;

        // Map the result data based on new API structure
        const mappedResult = {
          name: '', // Not provided in new structure
          seatNumber: data.result.seat_number || data.result.form_no || '',
          totalScore: '', // Not provided in new structure
          average: '', // Not provided in new structure
          result: data.result.result || '',
          subjects: null // Not provided in new structure
        };

        // Store result and navigate after toast
        sessionStorage.setItem('student_result', JSON.stringify(mappedResult));
        setTimeout(() => {
          showToast.value = false;
          // Since there are no subjects in the new structure, always navigate to simple result
          router.push({
            name: 'student-result',
            params: { result: mappedResult }
          });
        }, ALREADY_VERIFIED_TIMEOUT);
      } else {
        // Error or NO status
        showErrorToast(mappedMsg || data.message_ar || 'حدث خطأ أثناء الإرسال')
      }
    } else {
      // Fallback for unexpected response
      showErrorToast('حدث خطأ أثناء الإرسال')
    }
  } catch (error) {
    showErrorToast('فشل الاتصال بالخادم. حاول مرة أخرى.')
  } finally {
    loading.value = false;
  }
}

const handleVerificationSuccess = (result) => {
  showVerification.value = false
  showResult.value = true
  if (result && result.data) {
    // If result is wrapped in .data (from axios)
    realResult.value = result.data
  } else {
    realResult.value = result
  }
}

const handleReset = () => {
  showResult.value = false
  showVerification.value = false
  seatNumber.value = ''
  phoneNumber.value = ''
  selectedResultType.value = ''
  realResult.value = null
}

onMounted(() => {
  fetchConfig()
})
</script>

<template>
  <!-- Show offline page when system is not live -->
  <SystemOfflinePage v-if="!isLive" />

  <!-- Show form when system is live -->
  <div v-else class="container my-5">
    <div v-if="!showVerification && !showResult" class="row justify-content-center">
      <div class="col-12 col-md-8 col-lg-5">
        <form @submit.prevent="handleSubmit" class="rounded-4">
          <div class="mb-3">
            <input v-model="seatNumber" @input="handleSeatInput" :maxLengthSeat="maxLengthSeat" type="text"
              :placeholder="config?.text_box1 || 'ادخل رقم الجلوس او رقم الاستمارة'" class="form-control form-control-lg rounded-pill text-end mb-3 border-input"
              style="font-family: 'MTNBrighterSans', sans-serif;" />
          </div>
          <div class="mb-3">
            <input v-model="phoneNumber" @input="handlePhoneInput" :maxLengthPhone="maxLengthPhone" type="tel"
              placeholder="09xxxxxxxx رقم الهاتف"
              class="form-control form-control-lg rounded-pill text-end mb-3 border-input"
              style="font-family: 'MTNBrighterSans', sans-serif;" />
          </div>

      

          <div class="text-center text-color mb-4" style="font-family: 'MTNBrighterSans', sans-serif;">
            <p class="mb-1">
              سعر النتيجه {{ config?.price || '3525' }} {{ config?.currency || 'جنيه' }}   {{ config?.price_text_ar || 'سعر النتيجة' }}
              <!-- {{ config?.price_text_ar || 'سعر النتيجة' }} {{ config?.price || '3525' }} {{ config?.currency || 'جنيه' }} -->
            </p>
            <!-- <p class="small">({{ config?.price_text_ar }})</p> -->
          </div>
          <button type="submit" :disabled="loading"
            class="btn btn-dark btn-lg w-100 rounded-pill mb-2 text-test transition-colors"
            style="font-family: 'MTNBrighterSans', sans-serif;">
            <span v-if="loading">جاري التحقق...</span>
            <span v-else>طلب رمز التحقق</span>
          </button>
        </form>
      </div>
    </div>
    <div v-else-if="showVerification">
      <VerificationCode @verification-success="handleVerificationSuccess" @back="handleReset" />
    </div>
    <div v-else>
      <StudentResult :result="realResult" />
      <div class="text-center mt-4">
        <button @click="handleReset" class="btn btn-dark rounded-pill px-5"
          style="font-family: 'MTNBrighterSans', sans-serif;">
          عودة
        </button>
      </div>
    </div>
  </div>
  <AppToast v-if="showToast" :message="toastMessage" :type="toastType" @close="showToast = false" />
</template>

<style scoped>
.border-input {
  opacity: 1;
  border-color: rgb(0 0 0 / 1);
  border-width: 2px;
}

.form-control:focus {
  color: var(--bs-body-color);
  background-color: var(--bs-body-bg);
  border-color: #000;
  outline: 0;
  box-shadow: 0 0 0 .25rem rgba(255, 0, 0, 0.25);
}

.text-test {
  opacity: 1;
  color: rgb(255 196 3 / 1);
  font-size: 1rem;
}

.form-control-lg {
  min-height: calc(1.5em + 1rem + calc(var(--bs-border-width) * 2));
  padding: .5rem 1rem;
  font-size: 1rem;
  border-radius: var(--bs-border-radius-lg);
}
</style>